import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, CreditCard, Truck, CheckCircle2, DollarSign, Loader2, ChevronRight, Package } from "lucide-react";
import { toast } from "sonner";
import { onlinePaymentsEnabled, emailIntro, viewLabel } from "@/lib/onlinePayments";
import { emailPartsQuote, checkPartsPayment, shipPartsOrder } from "@/lib/api";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { docFilename } from "@/components/common/EmailDocMode";

// Single source of truth for presentational state + click-advance logic.
// Used identically in PartsCounter row, the quote-edit form, and Dashboard "Parts To Ship".

const STATUS_META = {
  open: { label: "Draft", next: "Email Quote", color: "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200", icon: Mail },
  submitted: { label: "Sent", next: "Check Payment", color: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100", icon: CreditCard },
  paid: { label: "Paid", next: "Ship", color: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100", icon: Truck },
  shipped: { label: "Shipped", next: "", color: "bg-slate-100 text-slate-500 border-slate-300 cursor-default", icon: CheckCircle2 },
};

export function PartsStatusButton({ order, onChanged, size = "sm", showNext = true, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const status = order.status || "open";
  const meta = STATUS_META[status] || STATUS_META.open;
  const Icon = meta.icon;

  const handleClick = async () => {
    if (status === "shipped") return;

    if (status === "open") {
      // Advance: email quote
      if (!order.email) { toast.error("Customer email required on quote"); return; }
      if ((order.total || 0) <= 0) { toast.error("Quote total must be greater than $0"); return; }
      setBusy(true);
      try {
        const res = await emailPartsQuote(order.id);
        const { short_code, order_number } = res.data;
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const quoteUrl = `${baseUrl}/api/r/q/${short_code}/${docFilename("Parts_Quote", order?.quote_number || order?.order_number || order?.id)}`;
        const payOn = res.data?.payments_enabled ?? await onlinePaymentsEnabled();
        const subject = encodeURIComponent(`Parts Quote ${order_number} — ${order.company}`);
        const body = encodeURIComponent(
          `Hi ${order.contact || "there"},\n\n` +
          `${emailIntro("parts quote", payOn)}\n\n` +
          `Quote ${order_number}\n` +
          `${viewLabel(payOn)}: ${quoteUrl}\n\n` +
          `Total: $${Number(order.total || 0).toFixed(2)}\n\n` +
          `Once payment is received we will ship your parts.\n\n` +
          `Thank you,\n${COMPANY_INFO.name}\n${COMPANY_INFO.phone}\n${COMPANY_INFO.email}`
        );
        window.location.href = `mailto:${order.email}?subject=${subject}&body=${body}`;
        toast.success("Opening email — status advanced to Sent");
        onChanged?.();
      } catch {
        toast.error("Failed to prepare quote email");
      } finally { setBusy(false); }
      return;
    }

    if (status === "submitted") {
      // Advance: check Square payment status
      setBusy(true);
      try {
        const res = await checkPartsPayment(order.id);
        if (res.data.status === "paid") {
          toast.success(`${order.order_number} — payment confirmed!`);
          onChanged?.();
        } else {
          // Not paid yet — just inform. User has the Mark Paid button in the quote view for overrides.
          toast.info("Not yet paid on Square. Open the quote and use Mark Paid if payment was received offline.");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Payment check failed");
      } finally { setBusy(false); }
      return;
    }

    if (status === "paid") {
      // Advance: open ship modal
      setShipOpen(true);
      return;
    }
  };

  return (
    <>
      <Button
        size={size}
        variant="outline"
        disabled={busy || status === "shipped"}
        onClick={handleClick}
        className={`h-7 text-[11px] border ${meta.color} ${className}`}
        data-testid={`status-btn-${order.id}`}
        title={status === "shipped" ? `Shipped${order.tracking_number ? ` — ${order.tracking_carrier} ${order.tracking_number}` : ""}` : `Click to: ${meta.next}`}
      >
        {busy ? (
          <Loader2 className="w-3 h-3 animate-spin mr-1" />
        ) : (
          <Icon className="w-3 h-3 mr-1" />
        )}
        <span className="font-medium">{meta.label}</span>
        {showNext && meta.next && (
          <>
            <ChevronRight className="w-3 h-3 mx-0.5 opacity-50" />
            <span className="opacity-80">{meta.next}</span>
          </>
        )}
      </Button>
      <PartsShipModal open={shipOpen} onOpenChange={setShipOpen} order={order} onShipped={onChanged} />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Ship Modal with tracking + "picked up" shortcut
// ──────────────────────────────────────────────────────────────

const CARRIER_TRACK_URLS = {
  UPS: (n) => `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(n)}`,
  FedEx: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  USPS: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  DHL: (n) => `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(n)}`,
};

export function PartsShipModal({ open, onOpenChange, order, onShipped }) {
  const [carrier, setCarrier] = useState("UPS");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [pickedUp, setPickedUp] = useState(false);
  const [customCarrier, setCustomCarrier] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCarrier("UPS"); setTrackingNumber(""); setNotes(""); setPickedUp(false); setCustomCarrier(""); };

  const effectiveCarrier = carrier === "Other" ? customCarrier.trim() : carrier;

  const handleShip = async () => {
    if (!pickedUp && !trackingNumber.trim()) { toast.error("Tracking number required (or mark as Picked Up)"); return; }
    if (!pickedUp && carrier === "Other" && !customCarrier.trim()) { toast.error("Enter carrier name"); return; }
    setBusy(true);
    try {
      // 1. Save tracking + mark shipped + archive — ALWAYS before opening mail client
      await shipPartsOrder(order.id, {
        tracking_carrier: pickedUp ? "Pickup" : effectiveCarrier,
        tracking_number: pickedUp ? "" : trackingNumber.trim(),
        tracking_notes: notes.trim(),
        picked_up: pickedUp,
      });

      // 2. Compose customer email (skip if no email on file)
      if (order.email) {
        const subject = encodeURIComponent(
          pickedUp
            ? `Parts Order ${order.order_number} — Ready / Picked Up`
            : `Your ABS Parts Order ${order.order_number} has shipped`
        );
        const trackUrl = !pickedUp && CARRIER_TRACK_URLS[effectiveCarrier]
          ? CARRIER_TRACK_URLS[effectiveCarrier](trackingNumber.trim())
          : "";
        const body = encodeURIComponent(
          pickedUp
            ? `Hi ${order.contact || "there"},\n\n` +
              `Your parts order ${order.order_number} has been picked up from our counter.\n\n` +
              (notes ? `Notes: ${notes}\n\n` : "") +
              `Thank you for your business!\n\n${COMPANY_INFO.name}\n${COMPANY_INFO.phone}\n${COMPANY_INFO.email}`
            : `Hi ${order.contact || "there"},\n\n` +
              `Your parts order ${order.order_number} has shipped.\n\n` +
              `Carrier: ${effectiveCarrier}\n` +
              `Tracking Number: ${trackingNumber.trim()}\n` +
              (trackUrl ? `Track here: ${trackUrl}\n\n` : "\n") +
              (notes ? `Notes: ${notes}\n\n` : "") +
              `Thank you for your business!\n\n${COMPANY_INFO.name}\n${COMPANY_INFO.phone}\n${COMPANY_INFO.email}`
        );
        window.location.href = `mailto:${order.email}?subject=${subject}&body=${body}`;
        toast.success(`${order.order_number} shipped — customer email opened`);
      } else {
        toast.success(`${order.order_number} marked shipped (no email on file)`);
      }
      onOpenChange(false);
      reset();
      onShipped?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to ship");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="ship-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-600" />
            Ship Parts Order {order?.order_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
            <div><span className="text-slate-400">Customer:</span> <span className="font-medium text-slate-700">{order?.company}</span></div>
            {order?.contact && <div><span className="text-slate-400">Contact:</span> {order.contact}</div>}
            {order?.email && <div><span className="text-slate-400">Email:</span> {order.email}</div>}
            <div><span className="text-slate-400">Ship to:</span> {[order?.ship_to_address, order?.ship_to_city, order?.ship_to_state, order?.ship_to_zip].filter(Boolean).join(", ") || "Walk-in"}</div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="picked-up-toggle">
            <input type="checkbox" checked={pickedUp} onChange={(e) => setPickedUp(e.target.checked)} className="h-4 w-4 accent-emerald-600" />
            <Package className="w-4 h-4 text-slate-400" />
            <span className="text-slate-700">Customer picked up at counter <span className="text-slate-400">(skip tracking)</span></span>
          </label>

          {!pickedUp && (
            <>
              <div>
                <Label className="text-xs font-medium">Carrier</Label>
                <Select value={carrier} onValueChange={setCarrier}>
                  <SelectTrigger className="mt-1" data-testid="carrier-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UPS">UPS</SelectItem>
                    <SelectItem value="FedEx">FedEx</SelectItem>
                    <SelectItem value="USPS">USPS</SelectItem>
                    <SelectItem value="DHL">DHL</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {carrier === "Other" && (
                  <Input value={customCarrier} onChange={(e) => setCustomCarrier(e.target.value)} placeholder="Carrier name" className="mt-2" data-testid="custom-carrier" />
                )}
              </div>
              <div>
                <Label className="text-xs font-medium">Tracking Number</Label>
                <Input
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  placeholder="Paste tracking # here"
                  className="mt-1 font-mono text-sm"
                  autoFocus
                  data-testid="tracking-number"
                />
              </div>
            </>
          )}

          <div>
            <Label className="text-xs font-medium">Notes <span className="text-slate-400">(optional)</span></Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., Shipped from Broomfield" className="mt-1" data-testid="ship-notes" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleShip} disabled={busy} data-testid="confirm-ship">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Truck className="w-4 h-4 mr-1" />}
              {pickedUp ? "Mark Picked Up" : "Ship & Email Customer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
